// Global search popover with debounce + 4 categories + Ctrl/Cmd+K shortcut.
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, HardHat, User as UserIcon, StickyNote, CheckSquare } from "lucide-react";
import { Input } from "../ui/input";
import { api } from "../../lib/api";

function useDebouncedValue(value, delay = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

const SECTIONS = [
  { key: "sites", label: "Chantiers", Icon: HardHat },
  { key: "users", label: "Utilisateurs", Icon: UserIcon },
  { key: "memos", label: "Mémos", Icon: StickyNote },
  { key: "tasks", label: "Tâches", Icon: CheckSquare },
];

export default function GlobalSearch() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const debounced = useDebouncedValue(q, 300);
  const inputRef = useRef(null);
  const wrapRef = useRef(null);

  // Raccourci clavier Ctrl/Cmd+K (fonctionnel, hint retiré du placeholder — V4-T19)
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      } else if (e.key === "Escape") {
        setOpen(false);
        inputRef.current?.blur();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Fermer au clic extérieur
  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const { data, isFetching } = useQuery({
    queryKey: ["global-search", debounced],
    queryFn: async () =>
      (await api.get("/search", { params: { q: debounced, limit: 8 } })).data,
    enabled: debounced.trim().length >= 1,
  });

  const go = (link) => {
    setOpen(false);
    setQ("");
    navigate(link);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    setOpen(false);
    navigate(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  const totalResults = data?.total ?? 0;

  return (
    <div ref={wrapRef} className="relative hidden sm:block">
      <form onSubmit={onSubmit}>
        <Search className="h-4 w-4 absolute left-3 top-2.5 text-muted-foreground pointer-events-none" />
        <Input
          ref={inputRef}
          data-testid="global-search-input"
          placeholder="Rechercher…"
          className="pl-9 w-56 lg:w-72 bg-muted border-border"
          value={q}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
        />
      </form>
      {open && debounced.trim() && (
        <div
          className="absolute right-0 mt-2 w-[28rem] max-w-[90vw] bg-card border border-border rounded-lg shadow-lg z-40 max-h-[60vh] overflow-auto"
          data-testid="global-search-dropdown"
        >
          {isFetching && !data && (
            <p className="p-4 text-sm text-muted-foreground">Recherche…</p>
          )}
          {data && totalResults === 0 && (
            <p className="p-4 text-sm text-muted-foreground" data-testid="global-search-empty">
              Aucun résultat pour &quot;{debounced}&quot;
            </p>
          )}
          {data && totalResults > 0 && (
            <div className="py-2">
              {SECTIONS.map(({ key, label, Icon }) => {
                const items = data[key] || [];
                if (items.length === 0) return null;
                return (
                  <div key={key} className="mb-2">
                    <p className="px-4 pt-2 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                      {label}
                    </p>
                    <ul>
                      {items.map((it) => (
                        <li key={`${key}-${it.id}`}>
                          <button
                            type="button"
                            onClick={() => go(it.link)}
                            className="w-full text-left px-4 py-2 hover:bg-muted flex items-start gap-3"
                            data-testid={`global-search-result-${key}-${it.id}`}
                          >
                            <Icon className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-foreground truncate">
                                {it.label}
                              </p>
                              {it.sublabel && (
                                <p className="text-xs text-muted-foreground truncate">
                                  {it.sublabel}
                                </p>
                              )}
                            </div>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
